import _ from 'lodash-es';
import Utils from '~/scripts/services/utils';
import templateExportModal from '~/pages/template-export/modal/template-export-modal.html';
import templateExportModalController from '~/pages/template-export/modal/template-export-modal';
import APIV5 from '~/models/api';
import templateModalForm from './modal/modal-form.html';
import ModalFormController from './modal/modal-form.controller';

/* @ngInject */
const methodService = (
	/** @type {import('angular').IHttpService} */ $http,
	/** @type {import('angular').ILocationService} */ $location,
	/** @type {import('angular').IQService} */ $q,
	/** @type {import('angular').IRootScopeService} */ $rootScope,
	/** @type {import('angular').ui.bootstrap.IModalService} */ $uibModal,
) => {
	/**
	 * @type {import('@knitiv/api-client-javascript').KNode}
	 */
	let localMethodKid;

	/**
	 * @type {import('@knitiv/api-client-javascript').KNode}
	 */
	let localNodeKid;

	/**
	 * @type {any}
	 */
	let localData;

	const checkForKeywords = (/** @type {any} */ parameters) => {
		const newParameters = parameters;
		for (const [k, parameter] of Object.entries(newParameters)) {
			// eslint-disable-next-line unicorn/prefer-switch
			if (parameter === '__SELF__') {
				newParameters[k] = localMethodKid;
			}
			else if (parameter === '__KID__') {
				newParameters[k] = localNodeKid;
			}
			else if (parameter === '__DATA__') {
				newParameters[k] = localData;
			}
		}
		return newParameters;
	};

	// noinspection JSUnusedGlobalSymbols
	/** @type {any} */
	const methods = {
		callAPI: (/** @type {any} */ parameters) => {
			_.merge(parameters, checkForKeywords(parameters.params));
			const { action } = parameters;
			delete parameters.action;
			// eslint-disable-next-line no-underscore-dangle
			return APIV5.instance.raw(
				action,
				parameters,
				undefined,
				{ id: 'method-service.call-api' },
			);
		},

		consoleLog: (/** @type {any} */ parameters) => {
			// eslint-disable-next-line no-console
			console.log(parameters);
			return $q.resolve(true);
		},

		download: (/** @type {*} */ parameters) => {
			if ('data' in parameters) {
				Utils.download(parameters.data, parameters.type, parameters.name);
				return $q.resolve(true);
			}
			if ('fileid' in parameters) {
				const url = APIV5.instance.downloadURL({
					nodeid: parameters.fileid,
				});
				return window.open(url, '_self');
			}
			if ('kid' in parameters) {
				const url = APIV5.instance.downloadURL({
					nodeid: parameters.node_kid,
				});
				return window.open(url, '_self');
			}
			if ('url' in parameters) {
				return $http.get(parameters.url);
			}

			return $q.reject();
		},

		knmExec: (/** @type {any} */ parameters) => {
			const newParameters = checkForKeywords(parameters);
			return APIV5.instance.knmExecute(
				{
					method_kid: newParameters.method_kid,
					node_kid:   newParameters.node_kid,
					params:     newParameters.params,
				},
				{ id: 'method-service.knm-exec' },
			);
		},

		locationPath: (/** @type {any} */ parameters) => {
			$location.path(parameters.path);
			return $q.resolve(true);
		},

		locationUrl: (/** @type {any} */ parameters) => {
			$location.url(parameters.url);
			return $q.resolve(true);
		},

		openModalForm: (/** @type {any} */ parameters) => {
			const modalOptions = {
				...parameters.options,
				ariaLabelledBy:  'modal-form-title',
				ariaDescribedBy: 'modal-form-body',
				template:        templateModalForm,
				controller:      ModalFormController,
				controllerAs:    '$ctrl',
				resolve:         {
					form:       () => parameters.form,
					options:    () => parameters.options || {},
				},
			};
			return $uibModal.open(modalOptions).result;
		},

		refreshForm: () => {
			$rootScope.$broadcast('Form.refresh');
			return $q.resolve(true);
		},

		templateExport: () => {
			const { result } = $uibModal.open({
				template: templateExportModal,
				size: 'lg',
				controller: templateExportModalController,
				windowClass: 'modal-form-select',
				controllerAs: '$ctrl',
				resolve: {
					node() {
						return localNodeKid;
					},
				},
			});

			return result
				.then(newResult => $q.when(
					APIV5.instance.kdgGenerate({
						format: 'PDF',
						node_kid: localNodeKid,
						template_kid: newResult.kid,
					}),
				))
				.catch(error => {
					if (error !== 'cancel') {
						console.error('e', error);
					}
				});
		},

		triggerEvent: (/** @type {any} */ parameters) => {
			$rootScope.$broadcast(parameters.event, parameters.params);
			return $q.resolve(true);
		},

		updateCurrentValue: (/** @type {any} */ parameters) => $q.resolve({
			action: 'updateCurrentValue',
			value: parameters,
		}),

		updateForm: (/** @type {any} */ parameters) => $q.resolve({ action: 'updateForm', data: parameters }),

		updateFormData: (/** @type {any} */ parameters) => $q.resolve({ action: 'updateFormData', data: parameters }),
	};

	// eslint-disable-next-line prefer-const
	const localAction = (
		/** @type {string} */ action,
		/** @type {any} */ parameters,
		/** @type {any} */ methodKid,
		/** @type {any} */ nodeKid,
		/** @type {any} */ data,
		// eslint-disable-next-line unicorn/prevent-abbreviations
		/** @type {any} */ def,
	) => {
		const deferred = def || $q.defer(); // Use def when it is recursive call

		localMethodKid = methodKid;
		localNodeKid = nodeKid;
		localData = data;

		if (action === 'knmExec') {
			_.merge(parameters, { node_kid: nodeKid }, parameters);
		}

		if (typeof methods[action] === 'function') {
			methods[action](parameters)
				.then(
					(/** @type {any} */ returnValue) => {
						if (typeof returnValue === 'object' && 'front' in returnValue) {
							for (const r of Object.values(returnValue.front)) {
								localAction(r.action, r.params, methodKid, nodeKid, r.data || data, deferred);
							}
						}
						else {
							deferred.resolve(returnValue);
						}
						return true;
					},
				).catch((/** @type {any} */ error) => {
					console.error(error);
					return deferred.reject('Error in action');
				});
		}
		else {
			deferred.reject('Unknown action');
		}

		return deferred.promise;
	};

	return {
		action: localAction,
		methods,
	};
};

export default methodService;
